import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, RotateCcw, Pencil, Share2, RefreshCw } from "lucide-react";
import api, { OCCASIONS, COLOR_FAMILIES, CATEGORIES, imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { prepareImage, PHOTO_ERROR_MSG } from "@/lib/image";
import { toAnalysis } from "@/pages/Wardrobe";
import UploadZone from "@/components/UploadZone";
import AnalyzingSequence from "@/components/AnalyzingSequence";
import ShareCard from "@/components/ShareCard";
import { Swatch } from "@/components/ColorSwatch";
import { assetForItem, pieceAssetColourAccurate } from "@/lib/categoryAssets";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_TONE = {
  "EXCELLENT MATCH": "text-[#C8A97E] border-[#C8A97E]",
  "STRONG MATCH": "text-[#C8A97E] border-[#C8A97E]/60",
  "ALMOST THERE": "text-[#F5F2EA] border-[#F5F2EA]/40",
  "DIFFICULT COMBINATION": "text-[#F5F2EA]/80 border-[#8A8170]",
};

const analyzeOne = async (file) => {
  const fd = new FormData(); fd.append("file", file);
  const { data } = await api.post("/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return { analysis: data.analysis, image_url: data.image_url };
};

export default function DontMessUp() {
  const { user } = useAuth();
  const debug = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1";
  const [step, setStep] = useState("upload"); // upload | analyzing | confirm | result
  const [a, setA] = useState({ file: null, preview: null, analysis: null });
  const [b, setB] = useState({ file: null, preview: null, analysis: null });
  const [occasion, setOccasion] = useState("");
  const [result, setResult] = useState(null);
  const [fix, setFix] = useState(null);        // {keep, recs}
  const [shareOpen, setShareOpen] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [picker, setPicker] = useState(null);  // 'a' | 'b' | null

  const setItem = (side, patch) => (side === "a" ? setA((p) => ({ ...p, ...patch })) : setB((p) => ({ ...p, ...patch })));

  // Track object URLs we own so each capture/retake revokes the previous blob (memory fix).
  const owned = useRef({ a: null, b: null });
  const revokeOwned = (side) => {
    if (owned.current[side]) { URL.revokeObjectURL(owned.current[side]); owned.current[side] = null; }
  };
  useEffect(() => () => { revokeOwned("a"); revokeOwned("b"); }, []);

  const setCapture = async (side, f) => {
    try {
      const prepared = await prepareImage(f);
      revokeOwned(side);
      const url = URL.createObjectURL(prepared);
      owned.current[side] = url;
      setItem(side, { file: prepared, preview: url, analysis: null });
    } catch { toast.error(PHOTO_ERROR_MSG); }
  };
  const clearSide = (side) => { revokeOwned(side); setItem(side, { file: null, preview: null, analysis: null }); };

  const pickFromWardrobe = (side, item) => {
    revokeOwned(side);
    setItem(side, { file: null, preview: imageUrl(item.image_url), analysis: toAnalysis(item) });
    setPicker(null);
  };

  const analyzeBoth = async () => {
    const aReady = a.file || a.analysis, bReady = b.file || b.analysis;
    if (!aReady || !bReady) { toast("Add both pieces first."); return; }
    setStep("analyzing");
    try {
      const [ra, rb] = await Promise.all([
        a.analysis ? Promise.resolve({ analysis: a.analysis }) : analyzeOne(a.file),
        b.analysis ? Promise.resolve({ analysis: b.analysis }) : analyzeOne(b.file),
      ]);
      setA((p) => ({ ...p, analysis: ra.analysis }));
      setB((p) => ({ ...p, analysis: rb.analysis }));
      setStep("confirm");
    } catch { toast.error("We couldn't read those. Try again."); setStep("upload"); }
  };

  const evaluate = async (occ = occasion) => {
    setFix(null); setStep("analyzing");
    try {
      const { data } = await api.post("/dont-mess-up", { item_a: a.analysis, item_b: b.analysis, occasion: occ || null });
      setResult(data);
      setTimeout(() => setStep("result"), data.belong ? 1100 : 600);
    } catch { toast.error("Couldn't evaluate that combination."); setStep("confirm"); }
  };

  const runFix = async (keepSide) => {
    const keep = keepSide === "a" ? a.analysis : b.analysis;
    const other = keepSide === "a" ? b.analysis : a.analysis;
    try {
      const { data } = await api.post("/dont-mess-up/fix", {
        keep, other_category: other.category || "Complete Look", occasion: occasion || null,
      });
      setFix({ keepSide, keepName: keep.primary_color_name + " " + keep.item, recs: data.recommendations });
    } catch { toast.error("Couldn't fetch alternatives."); }
  };

  const reset = () => { revokeOwned("a"); revokeOwned("b"); setA({ file: null, preview: null, analysis: null }); setB({ file: null, preview: null, analysis: null }); setResult(null); setFix(null); setStep("upload"); };
  const changeItem = (side) => { clearSide(side); setResult(null); setStep("upload"); };

  const openShare = () => {
    setShareData({ baseColor: a.analysis.primary_color_name, baseHex: a.analysis.primary_color_hex,
      matchColor: b.analysis.primary_color_name, matchHex: b.analysis.primary_color_hex,
      score: result.score, statusText: result.phrase });
    setShareOpen(true);
  };

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-20 min-h-[70vh]">
      {step === "upload" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] text-center">Don't Mess Up</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] text-center mt-3">Do these belong together?</h1>
          <p className="font-sans text-sm text-[#8A8170] text-center mt-4 mb-12 max-w-md mx-auto leading-relaxed">
            Two pieces. One question. Do they work? MOIRÉ will tell you honestly — and how to sharpen it.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] text-center mb-3">Item One</p>
              <UploadZone testid="dmu-a" label="First piece" onFile={(f) => setCapture("a", f)}
                preview={a.preview} onClear={() => clearSide("a")} />
              {user && !a.preview && (
                <button onClick={() => setPicker("a")} data-testid="dmu-a-wardrobe"
                  className="w-full mt-3 border border-[#F5F2EA]/15 py-2.5 font-sans text-xs tracking-wider uppercase hover:border-[#C8A97E] transition-colors">Choose From Wardrobe</button>
              )}
            </div>
            <div>
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] text-center mb-3">Item Two</p>
              <UploadZone testid="dmu-b" label="Second piece" onFile={(f) => setCapture("b", f)}
                preview={b.preview} onClear={() => clearSide("b")} />
              {user && !b.preview && (
                <button onClick={() => setPicker("b")} data-testid="dmu-b-wardrobe"
                  className="w-full mt-3 border border-[#F5F2EA]/15 py-2.5 font-sans text-xs tracking-wider uppercase hover:border-[#C8A97E] transition-colors">Choose From Wardrobe</button>
              )}
            </div>
          </div>
          {(a.file || a.analysis) && (b.file || b.analysis) && (
            <div className="flex justify-center mt-10">
              <button onClick={analyzeBoth} data-testid="dmu-check"
                className="flex items-center gap-3 bg-[#C8A97E] text-[#0D0D0D] px-10 py-4 font-sans text-sm tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">
                Check My Combination <ArrowRight size={16} strokeWidth={2} />
              </button>
            </div>
          )}
        </motion.div>
      )}

      {step === "analyzing" && <AnalyzingSequence stages={["Looking at both pieces…", "Reading the colours…", "Weighing the balance…"]}
        done={result?.belong} />}

      {step === "confirm" && a.analysis && b.analysis && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] text-center">MOIRÉ sees</p>
          <h2 className="font-serif text-3xl text-[#F5F2EA] text-center mt-2 mb-8">Looks right?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <ItemConfirm label="Item One" item={a} onChange={(patch) => setItem("a", { analysis: { ...a.analysis, ...patch } })} />
            <ItemConfirm label="Item Two" item={b} onChange={(patch) => setItem("b", { analysis: { ...b.analysis, ...patch } })} />
          </div>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-10">
            <button onClick={() => evaluate()} data-testid="dmu-confirm"
              className="flex items-center gap-3 bg-[#C8A97E] text-[#0D0D0D] px-10 py-4 font-sans text-sm tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">
              Check The Match <ArrowRight size={16} strokeWidth={2} />
            </button>
            <button onClick={reset} className="font-sans text-xs tracking-luxe uppercase text-[#8A8170] hover:text-[#C8A97E]">Start over</button>
          </div>
        </motion.div>
      )}

      {step === "result" && result && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center border border-[#C8A97E]/40 bg-[#111111] p-10 md:p-14">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">MOIRÉ Match</p>
            <div className="font-serif text-7xl text-[#C8A97E] mt-2">{Number(result.score).toFixed(1)}</div>
            <div className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">/ 10</div>
            <div className={`inline-block mt-6 px-5 py-2 border font-sans text-xs tracking-luxe uppercase ${STATUS_TONE[result.status]}`} data-testid="dmu-status">
              {result.status}
            </div>
            <div className="flex items-center justify-center gap-4 mt-8">
              {[a, b].map((side, idx) => {
                const sImg = side.preview || pieceAssetColourAccurate(side.analysis);
                return (
                  <React.Fragment key={idx}>
                    {idx === 1 && <span className="font-serif text-2xl text-[#8A8170]">+</span>}
                    <div className="text-center">
                      <span className="relative block h-24 w-24 overflow-hidden border border-[#F5F2EA]/15 bg-[#F5F2EA]" data-testid={`dmu-piece-${idx}`}>
                        {sImg ? <img src={sImg} alt={side.analysis.item} className="w-full h-full object-cover" />
                          : <span className="block w-full h-full" style={{ backgroundColor: side.analysis.primary_color_hex }} />}
                        <span className="absolute bottom-1 right-1 h-3 w-3 rounded-full border border-white/70" style={{ backgroundColor: side.analysis.primary_color_hex }} />
                      </span>
                      <div className="font-sans text-xs text-[#8A8170] mt-2">{side.analysis.primary_color_name}</div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <p className="font-serif text-2xl text-[#F5F2EA] mt-8" data-testid="dmu-phrase">{result.phrase}</p>
            <p className="font-serif italic text-lg text-[#F5F2EA]/80 mt-3 max-w-lg mx-auto">{result.verdict}</p>
            {result.correction && (
              <div className="mt-6 mx-auto max-w-md border border-[#C8A97E]/30 bg-[#0D0D0D] px-5 py-4" data-testid="dmu-correction">
                <p className="font-serif text-lg text-[#C8A97E]">{result.correction.headline}</p>
                <p className="font-sans text-xs text-[#8A8170] mt-1.5 leading-relaxed">{result.correction.reason}</p>
              </div>
            )}
            {debug && result.sub_scores && (
              <div className="mt-5 mx-auto max-w-md border border-dashed border-[#8A8170]/40 px-4 py-3 text-left" data-testid="dmu-debug">
                <p className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] mb-2">Debug · score components</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {Object.entries(result.sub_scores).map(([k, v]) => (
                    <div key={k} className="flex justify-between font-mono text-[11px] text-[#F5F2EA]/70">
                      <span>{k}</span><span>{Number(v).toFixed(2)}</span>
                    </div>))}
                </div>
              </div>
            )}
          </div>

          {/* occasion recompute */}
          <div className="flex items-center justify-center gap-3 mt-8">
            <span className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Wearing this to</span>
            <Select value={occasion || "none"} onValueChange={(v) => { const o = v === "none" ? "" : v; setOccasion(o); evaluate(o); }}>
              <SelectTrigger data-testid="dmu-occasion" className="w-48 bg-transparent border-[#F5F2EA]/15 rounded-none font-sans text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#F5F2EA]/15 text-[#F5F2EA]">
                <SelectItem value="none">Anywhere</SelectItem>
                {OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {result.belong ? (
            /* FINISH THE LOOK */
            <div className="mt-12 border border-[#F5F2EA]/10 bg-[#111111] p-8">
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Finish the look</p>
              <ul className="mt-4 space-y-1.5">
                {result.finish.items.map((it, i) => (
                  <li key={i} className="font-serif text-xl text-[#F5F2EA] flex items-center gap-3">
                    <span className="h-1 w-1 bg-[#C8A97E] rounded-full" /> {it}
                  </li>))}
              </ul>
              <p className="font-serif italic text-[#8A8170] mt-4">{result.finish.note}</p>
            </div>
          ) : (
            /* FIX THE LOOK + KEEP BOTH */
            <div className="mt-12 grid md:grid-cols-2 gap-6">
              <div className="border border-[#F5F2EA]/10 bg-[#111111] p-8">
                <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Fix the look</p>
                <p className="font-sans text-sm text-[#8A8170] mt-2">Which piece do you want to keep?</p>
                <div className="flex gap-3 mt-4">
                  <button onClick={() => runFix("a")} data-testid="fix-keep-a"
                    className="flex-1 border border-[#F5F2EA]/15 px-3 py-2.5 font-sans text-xs tracking-wider uppercase hover:border-[#C8A97E] transition-colors">
                    Keep {a.analysis.item}</button>
                  <button onClick={() => runFix("b")} data-testid="fix-keep-b"
                    className="flex-1 border border-[#F5F2EA]/15 px-3 py-2.5 font-sans text-xs tracking-wider uppercase hover:border-[#C8A97E] transition-colors">
                    Keep {b.analysis.item}</button>
                </div>
                {fix && (
                  <div className="mt-5" data-testid="fix-results">
                    <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-3">Try instead — for the other piece</p>
                    <div className="space-y-2">
                      {fix.recs.filter((r) => r.tier === "Best").map((r) => (
                        <div key={r.rank} className="flex items-center gap-3 border-b border-[#F5F2EA]/10 pb-2">
                          <span className="h-8 w-8 border border-[#F5F2EA]/15" style={{ backgroundColor: r.color_hex }} />
                          <span className="font-serif text-lg text-[#F5F2EA] flex-1">{r.color_name}</span>
                          <span className="font-serif text-xl text-[#C8A97E]">{Number(r.score).toFixed(1)}</span>
                        </div>))}
                    </div>
                  </div>)}
              </div>
              <div className="border border-[#F5F2EA]/10 bg-[#111111] p-8">
                <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">I want to keep both</p>
                <p className="font-serif italic text-[#F5F2EA]/85 mt-3 leading-relaxed">{result.keep_both.note}</p>
                <ul className="mt-4 space-y-1.5">
                  {result.keep_both.items.map((it, i) => (
                    <li key={i} className="font-sans text-sm text-[#F5F2EA]/80 flex items-center gap-3">
                      <span className="h-1 w-1 bg-[#C8A97E] rounded-full" /> {it}</li>))}
                </ul>
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-3 mt-12">
            <button onClick={openShare} data-testid="dmu-share"
              className="flex items-center gap-2 border border-[#F5F2EA]/15 px-6 py-3 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">
              <Share2 size={14} strokeWidth={1.5} /> Share</button>
            <button onClick={() => changeItem("a")} data-testid="dmu-change-a"
              className="border border-[#F5F2EA]/15 px-6 py-3 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">Change Item One</button>
            <button onClick={() => changeItem("b")} data-testid="dmu-change-b"
              className="border border-[#F5F2EA]/15 px-6 py-3 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">Change Item Two</button>
            <button onClick={reset} data-testid="dmu-reset"
              className="flex items-center gap-2 border border-[#C8A97E] text-[#C8A97E] px-6 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors">
              <RefreshCw size={14} strokeWidth={1.5} /> Try Another Combination</button>
          </div>
        </motion.div>
      )}

      <ShareCard open={shareOpen} onOpenChange={setShareOpen} data={shareData} />
      {picker && <WardrobePickerDialog side={picker} onClose={() => setPicker(null)} onPick={pickFromWardrobe} />}
    </div>
  );
}

function WardrobePickerDialog({ side, onClose, onPick }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/wardrobe").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0D0D0D] border border-[#C8A97E]/40 max-w-lg max-h-[80vh] overflow-y-auto" data-testid="dmu-wardrobe-picker">
        <DialogTitle className="font-serif text-2xl text-[#F5F2EA] font-normal">Choose from your wardrobe</DialogTitle>
        <DialogDescription className="sr-only">Pick a saved wardrobe piece for this slot.</DialogDescription>
        {items && items.length === 0 && <p className="font-sans text-sm text-[#8A8170] mt-2">Your wardrobe is empty. Add pieces in My Wardrobe first.</p>}
        <div className="grid grid-cols-3 gap-3 mt-3">
          {items?.map((it) => (
            <button key={it.id} onClick={() => onPick(side, it)} data-testid={`picker-item-${it.id}`}
              className="border border-[#F5F2EA]/10 hover:border-[#C8A97E] transition-colors overflow-hidden text-left">
              <div className="aspect-[3/4] bg-[#1A1A1A]">
                {it.image_url ? <img src={imageUrl(it.image_url)} alt={it.name} loading="lazy" className="w-full h-full object-cover" />
                  : (assetForItem({ category: it.category, name: it.name, color_name: it.primary_color_name, color_hex: it.primary_color_hex })
                      ? <img src={assetForItem({ category: it.category, name: it.name, color_name: it.primary_color_name, color_hex: it.primary_color_hex })} alt={it.name} loading="lazy" className="w-full h-full object-cover" />
                      : <div className="w-full h-full" style={{ backgroundColor: it.primary_color_hex }} />)}
              </div>
              <div className="p-2 font-serif text-sm text-[#F5F2EA] truncate">{it.name}</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ItemConfirm({ label, item, onChange }) {
  const [editing, setEditing] = useState(false);
  const an = item.analysis;
  return (
    <div className="border border-[#F5F2EA]/10 bg-[#111111] p-5" data-testid={`confirm-${label.replace(/\s/g, "").toLowerCase()}`}>
      <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">{label}</p>
      {item.preview && <img src={item.preview} alt={an.item} className="w-full h-40 object-cover border border-[#F5F2EA]/10 mt-3" />}
      {editing ? (
        <div className="space-y-3 mt-4">
          <Select value={CATEGORIES.find((c) => c.toLowerCase() === an.item.toLowerCase()) || an.item} onValueChange={(v) => onChange({ item: v })}>
            <SelectTrigger className="bg-transparent border-[#F5F2EA]/20 rounded-none text-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#F5F2EA]/15 text-[#F5F2EA] max-h-56">
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <input type="color" value={an.primary_color_hex} onChange={(e) => onChange({ primary_color_hex: e.target.value })}
              className="h-9 w-11 bg-transparent border border-[#F5F2EA]/20 cursor-pointer" />
            <Input value={an.primary_color_name} onChange={(e) => onChange({ primary_color_name: e.target.value })}
              className="bg-transparent border-[#F5F2EA]/20 rounded-none text-[#F5F2EA] text-sm" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {COLOR_FAMILIES.map((f) => (
              <button key={f.name} title={f.name} onClick={() => onChange({ primary_color_hex: f.hex, primary_color_name: f.name })}
                className="h-6 w-6 border border-[#F5F2EA]/15 hover:border-[#C8A97E]" style={{ backgroundColor: f.hex }} />))}
          </div>
          <button onClick={() => setEditing(false)} className="font-sans text-xs tracking-luxe uppercase text-[#C8A97E]">Done</button>
        </div>
      ) : (
        <div className="mt-4">
          <div className="flex items-center gap-3">
            <Swatch hex={an.primary_color_hex} size="md" />
            <div><div className="font-serif text-xl text-[#F5F2EA]">{an.item}</div>
              <div className="font-sans text-xs text-[#8A8170]">{an.primary_color_name}</div></div>
          </div>
          <button onClick={() => setEditing(true)} data-testid={`adjust-${label.replace(/\s/g, "").toLowerCase()}`}
            className="flex items-center gap-2 mt-3 font-sans text-xs tracking-luxe uppercase text-[#C8A97E] hover:text-[#E5C293]">
            <Pencil size={12} strokeWidth={1.5} /> Adjust</button>
        </div>
      )}
    </div>
  );
}
